import type { Static } from '@sinclair/typebox';
import { Type } from '@sinclair/typebox';

// ============================================================================
// ADMIN USER MANAGEMENT TYPES
// ============================================================================

export const AdminUserSchema = Type.Object({
  id: Type.String({ format: 'uuid' }),
  email_address: Type.String(),
  username: Type.String(),
  avatar: Type.Union([Type.String(), Type.Null()]),
  is_admin: Type.Boolean(),
  is_verified: Type.Boolean(),
  scan_credit: Type.Number(),
  topup_scan_credit: Type.Number(),
  created_at: Type.String(),
  updated_at: Type.String(),
  subscription: Type.Union([
    Type.Object({
      plan_id: Type.Number(),
      status_id: Type.Number(),
      payment_gateway: Type.String(),
      billing_interval: Type.String(),
      billing_cycle_start: Type.String(),
      billing_cycle_end: Type.String(),
      plan: Type.Object({
        id: Type.Number(),
        name: Type.String(),
        scan_quota: Type.Number(),
        link_quota: Type.Number(),
        annotation_quota: Type.Number(),
      }),
    }),
    Type.Null(),
  ]),
  shops: Type.Optional(
    Type.Array(
      Type.Object({
        id: Type.String({ format: 'uuid' }),
        platform: Type.String(),
        name: Type.Union([Type.String(), Type.Null()]),
        domain: Type.Union([Type.String(), Type.Null()]),
        email_address: Type.Union([Type.String(), Type.Null()]),
        shopify_shop_id: Type.Union([Type.String(), Type.Null()]),
        created_at: Type.String(),
      })
    )
  ),
  _count: Type.Optional(
    Type.Object({
      items: Type.Number(),
      jobs: Type.Number(),
    })
  ),
});
export type AdminUser = Static<typeof AdminUserSchema>;

export const GetUsersQuerySchema = Type.Object({
  page: Type.Optional(Type.Number({ minimum: 1 })),
  limit: Type.Optional(Type.Number({ minimum: 1, maximum: 100 })),
  search: Type.Optional(Type.String()),
  plan_id: Type.Optional(Type.Number()),
  is_admin: Type.Optional(Type.Boolean()),
  sort_by: Type.Optional(
    Type.Union([
      Type.Literal('created_at'),
      Type.Literal('username'),
      Type.Literal('email_address'),
      Type.Literal('scan_credit'),
    ])
  ),
  sort_order: Type.Optional(
    Type.Union([Type.Literal('asc'), Type.Literal('desc')])
  ),
});
export type GetUsersQuery = Static<typeof GetUsersQuerySchema>;

export const GetUsersResponseSchema = Type.Object({
  data: Type.Object({
    users: Type.Array(AdminUserSchema),
    total: Type.Number(),
    page: Type.Number(),
    limit: Type.Number(),
  }),
});
export type GetUsersResponse = Static<typeof GetUsersResponseSchema>;

export const UpdateUserBodySchema = Type.Object({
  username: Type.Optional(Type.String()),
  email_address: Type.Optional(Type.String({ format: 'email' })),
  is_admin: Type.Optional(Type.Boolean()),
  is_verified: Type.Optional(Type.Boolean()),
  scan_credit: Type.Optional(Type.Number()),
  topup_scan_credit: Type.Optional(Type.Number()),
});
export type UpdateUserBody = Static<typeof UpdateUserBodySchema>;

export const CreateUserBodySchema = Type.Object({
  email_address: Type.String({ format: 'email' }),
  password: Type.String({ minLength: 8 }),
  plan_id: Type.Optional(Type.Number()),
});
export type CreateUserBody = Static<typeof CreateUserBodySchema>;

export const UpdateUserResponseSchema = Type.Object({
  data: AdminUserSchema,
});
export type UpdateUserResponse = Static<typeof UpdateUserResponseSchema>;

export const CreateUsersBulkBodySchema = Type.Object({
  emails: Type.Array(Type.String({ format: 'email' }), { minItems: 1 }),
  password: Type.String({ minLength: 8 }),
  plan_id: Type.Optional(Type.Number()),
});
export type CreateUsersBulkBody = Static<typeof CreateUsersBulkBodySchema>;

export const CreateUsersBulkResultSchema = Type.Object({
  email: Type.String(),
  status: Type.Union([
    Type.Literal('created'),
    Type.Literal('skipped'),
    Type.Literal('failed'),
  ]),
  reason: Type.Optional(Type.String()),
});
export type CreateUsersBulkResult = Static<typeof CreateUsersBulkResultSchema>;

export const CreateUsersBulkResponseSchema = Type.Object({
  data: Type.Object({
    results: Type.Array(CreateUsersBulkResultSchema),
  }),
});
export type CreateUsersBulkResponse = Static<
  typeof CreateUsersBulkResponseSchema
>;

export const ChangeUserPlanBodySchema = Type.Object({
  plan_id: Type.Number(),
  billing_interval: Type.Union([
    Type.Literal('MONTHLY'),
    Type.Literal('YEARLY'),
    Type.Literal('NONE'),
  ]),
});
export type ChangeUserPlanBody = Static<typeof ChangeUserPlanBodySchema>;

// ============================================================================
// ADMIN ANALYTICS TYPES
// ============================================================================

// Upload Analytics
export const UploadStatsSchema = Type.Object({
  total_uploads: Type.Number(),
  successful_uploads: Type.Number(),
  failed_uploads: Type.Number(),
  in_progress_uploads: Type.Number(), // PENDING + RUNNING jobs
  avg_stw_processing_time: Type.Number(), // in seconds (STW upload to Kiri)
  avg_kiri_processing_time: Type.Number(), // in seconds (Kiri processing)
  uploads_by_type: Type.Array(
    Type.Object({
      type: Type.String(),
      count: Type.Number(),
    })
  ),
  uploads_over_time: Type.Array(
    Type.Object({
      date: Type.String(),
      count: Type.Number(),
    })
  ),
  top_uploaders: Type.Array(
    Type.Object({
      user_id: Type.String({ format: 'uuid' }),
      username: Type.String(),
      upload_count: Type.Number(),
      avg_processing_time: Type.Number(),
    })
  ),
  period_start: Type.String(), // Start date of the period
});
export type UploadStats = Static<typeof UploadStatsSchema>;

export const GetUploadAnalyticsResponseSchema = Type.Object({
  data: UploadStatsSchema,
});
export type GetUploadAnalyticsResponse = Static<
  typeof GetUploadAnalyticsResponseSchema
>;

// User Analytics
export const UserStatsSchema = Type.Object({
  total_users: Type.Number(),
  active_users: Type.Number(), // users active in period
  new_users_this_period: Type.Number(),
  users_by_plan: Type.Array(
    Type.Object({
      plan_id: Type.Number(),
      plan_name: Type.String(),
      user_count: Type.Number(),
    })
  ),
  daily_signups: Type.Array(
    Type.Object({
      date: Type.String(),
      count: Type.Number(),
    })
  ),
  cumulative_growth: Type.Array(
    Type.Object({
      date: Type.String(),
      total: Type.Number(),
    })
  ),
  total_credits_in_circulation: Type.Object({
    scan_credit: Type.Number(),
    topup_scan_credit: Type.Number(),
    total: Type.Number(),
  }),
  period_start: Type.String(), // Start date of the period
});
export type UserStats = Static<typeof UserStatsSchema>;

export const GetUserAnalyticsResponseSchema = Type.Object({
  data: UserStatsSchema,
});
export type GetUserAnalyticsResponse = Static<
  typeof GetUserAnalyticsResponseSchema
>;

// Activity Feed
export const ActivityItemSchema = Type.Object({
  id: Type.String({ format: 'uuid' }),
  type: Type.Union([
    Type.Literal('job_completed'),
    Type.Literal('user_signup'),
    Type.Literal('subscription_change'),
  ]),
  timestamp: Type.String(),
  data: Type.Any(), // Flexible data structure per activity type
});
export type ActivityItem = Static<typeof ActivityItemSchema>;

export const GetActivityQuerySchema = Type.Object({
  limit: Type.Optional(Type.Number({ minimum: 1, maximum: 100 })),
  type: Type.Optional(
    Type.Union([
      Type.Literal('job_completed'),
      Type.Literal('user_signup'),
      Type.Literal('subscription_change'),
    ])
  ),
});
export type GetActivityQuery = Static<typeof GetActivityQuerySchema>;

export const GetActivityResponseSchema = Type.Object({
  data: Type.Object({
    activities: Type.Array(ActivityItemSchema),
  }),
});
export type GetActivityResponse = Static<typeof GetActivityResponseSchema>;

// ============================================================================
// ADMIN ITEM MANAGEMENT TYPES
// ============================================================================

export const TransferItemBodySchema = Type.Object({
  new_owner_user_id: Type.String({ format: 'uuid' }),
});
export type TransferItemBody = Static<typeof TransferItemBodySchema>;

export const TransferItemResponseSchema = Type.Object({
  data: Type.Object({
    item_id: Type.String({ format: 'uuid' }),
    old_owner_user_id: Type.Union([
      Type.String({ format: 'uuid' }),
      Type.Null(),
    ]),
    new_owner_user_id: Type.String({ format: 'uuid' }),
  }),
});
export type TransferItemResponse = Static<typeof TransferItemResponseSchema>;

// ============================================================================
// ADMIN PLAN MANAGEMENT TYPES
// ============================================================================

export const PlanSchema = Type.Object({
  id: Type.Number(),
  name: Type.String(),
  link_quota: Type.Number(),
  scan_quota: Type.Number(),
  annotation_quota: Type.Number(),
  embed_quota: Type.Number(),
  storage_quota: Type.Number(),
  scene_quota: Type.Number(),
});
export type Plan = Static<typeof PlanSchema>;

export const GetPlansResponseSchema = Type.Object({
  data: Type.Object({
    plans: Type.Array(PlanSchema),
  }),
});
export type GetPlansResponse = Static<typeof GetPlansResponseSchema>;

export const UpdatePlanBodySchema = Type.Object({
  link_quota: Type.Optional(Type.Number({ minimum: 0 })),
  scan_quota: Type.Optional(Type.Number({ minimum: 0 })),
  annotation_quota: Type.Optional(Type.Number({ minimum: 0 })),
  embed_quota: Type.Optional(Type.Number({ minimum: 0 })),
  storage_quota: Type.Optional(Type.Number({ minimum: 0 })),
  scene_quota: Type.Optional(Type.Number({ minimum: 0 })),
});
export type UpdatePlanBody = Static<typeof UpdatePlanBodySchema>;

export const UpdatePlanResponseSchema = Type.Object({
  data: Type.Object({
    plan: PlanSchema,
  }),
});
export type UpdatePlanResponse = Static<typeof UpdatePlanResponseSchema>;

// ============================================================================
// UNIFIED COLLECTION (Items + Scenes)
// ============================================================================

export const CollectionSchema = Type.Object({
  id: Type.String({ format: 'uuid' }),
  name: Type.String(),
  thumbnail_url: Type.String(),
  order: Type.Number(),
  content_type: Type.Union([Type.Literal('item'), Type.Literal('scene')]),
  model_type: Type.Optional(Type.String()),
  owner: Type.Optional(
    Type.Object({
      id: Type.String({ format: 'uuid' }),
      username: Type.String(),
      avatar: Type.Union([Type.String(), Type.Null()]),
    })
  ),
});
export type Collection = Static<typeof CollectionSchema>;

export const GetCollectionResponseSchema = Type.Object({
  data: Type.Array(CollectionSchema),
});
export type GetCollectionResponse = Static<typeof GetCollectionResponseSchema>;

export const UpdateCollectionBodySchema = Type.Object({
  content: Type.Array(
    Type.Object({
      id: Type.String({ format: 'uuid' }),
      content_type: Type.Union([Type.Literal('item'), Type.Literal('scene')]),
    })
  ),
});
export type UpdateCollectionBody = Static<typeof UpdateCollectionBodySchema>;

// ============================================================================
// ITEM MODERATION TYPES
// ============================================================================

export enum ITEM_MODERATION_ACTION {
  BAN = 'BAN',
  UNBAN = 'UNBAN',
}

export const BanItemBodySchema = Type.Object({
  reason: Type.String({ minLength: 1 }),
});
export type BanItemBody = Static<typeof BanItemBodySchema>;

export const UnbanItemBodySchema = Type.Object({
  reason: Type.Optional(Type.String()),
});
export type UnbanItemBody = Static<typeof UnbanItemBodySchema>;

export const ItemModerationActionSchema = Type.Object({
  id: Type.String({ format: 'uuid' }),
  item_id: Type.String({ format: 'uuid' }),
  actor_id: Type.String({ format: 'uuid' }),
  action: Type.String(),
  reason: Type.Union([Type.String(), Type.Null()]),
  created_at: Type.String(),
  actor: Type.Object({
    id: Type.String({ format: 'uuid' }),
    username: Type.String(),
  }),
});
export type ItemModerationActionResponse = Static<
  typeof ItemModerationActionSchema
>;

export const GetItemModerationHistoryResponseSchema = Type.Object({
  data: Type.Object({
    actions: Type.Array(ItemModerationActionSchema),
  }),
});

export const AdminItemListQuerySchema = Type.Object({
  search: Type.Optional(Type.String()),
  isBanned: Type.Optional(Type.Boolean()),
  page: Type.Integer({ default: 1 }),
  pageSize: Type.Integer({ default: 32 }),
});

export const AdminItemListItemSchema = Type.Object({
  id: Type.String({ format: 'uuid' }),
  name: Type.String(),
  thumbnail_url: Type.String(),
  status_id: Type.Number(),
  is_banned: Type.Boolean(),
  banned_at: Type.Union([Type.String(), Type.Null()]),
  banned_reason: Type.Union([Type.String(), Type.Null()]),
  owner: Type.Union([
    Type.Object({
      id: Type.String({ format: 'uuid' }),
      username: Type.String(),
      email_address: Type.String(),
    }),
    Type.Null(),
  ]),
});

export const GetAdminItemListResponseSchema = Type.Object({
  data: Type.Object({
    items: Type.Array(AdminItemListItemSchema),
    totalItems: Type.Number(),
    pageSize: Type.Number(),
    totalPages: Type.Number(),
  }),
});
